<template>
  <div v-if="!$root.isMobile" class="el-users">
    <workspace-layout v-if="container.can_edit">
      <template v-slot:breadcrumbs>
        <el-breadcrumbs :parents="container.parents" :current="container"></el-breadcrumbs>
      </template>

      <template v-slot:actions>
        <button v-if="selectedUsers.length > 0" :disabled="loading" @click.prevent="removeUser" class="btn btn-outline-danger d-flex">
          <i class="fal fa-trash mr-15 my-auto"></i>
          <span class="my-auto" v-html="$t(`${$options.name}.remove`)"></span>
        </button>

        <button :disabled="loading" @click.prevent="addDistributionGroup" class="btn btn-primary ml-15 d-flex">
          <i class="fal fa-pencil mr-15 my-auto"></i>
          <span class="my-auto" v-html="$t(`${$options.name}.add_distribution_group`)"></span>
        </button>

        <button :disabled="loading" @click.prevent="inviteUser" class="btn btn-primary ml-15 d-flex">
          <i class="fal fa-plus mr-15 my-auto"></i>
          <span class="my-auto" v-html="$t(`${$options.name}.invite`)"></span>
        </button>
      </template>

      <template v-slot:page>
        <div class="row position-relative">
          <vue-good-table class="col-12 mt-30" :columns="columns" :search-options="{ enabled: true }" :rows="users" @on-selected-rows-change="selectionChanged" :select-options="{ enabled: true }" styleClass="vgt-table">
            <template slot="table-row" slot-scope="slotProps">
              <span v-if="slotProps.column.field == 'admin'" @click.stop>
                <div class="d-flex align-items-center">
                  <div class="custom-control custom-checkbox d-inline-block" :data-tooltip="slotProps.row.id === user.id ? $t(`${$options.name}.cant_change_own_role`) : slotProps.row.is_container_owner ? $t(`${$options.name}.owners_are_always_admin`) : false">
                    <input
                      :disabled="slotProps.row.id === user.id || slotProps.row.is_container_owner || !!slotProps.row.read_only || slotProps.row.in_distribution_groups"
                      @change="handleUserRoleUpdated(slotProps.row)"
                      type="checkbox"
                      class="custom-control-input"
                      :id="`${slotProps.row.id}_adminCheckbox`"
                      v-model="slotProps.row.is_container_admin"
                    />
                    <label class="custom-control-label" :for="`${slotProps.row.id}_adminCheckbox`">
                      <span class="sr-only">{{ slotProps.row.is_container_admin ? 'Yes' : 'No' }}</span>
                    </label>
                  </div>
                  <span v-if="slotProps.row.is_container_owner">{{ $t(`${$options.name}.owner`) }}</span>
                </div>
              </span>
              <span v-else-if="slotProps.column.field === 'group'" class="d-flex flex-row align-contents-center">
                <span v-if="slotProps.row.in_distribution_groups" v-for="group_id in slotProps.row.distribution_group_ids" :key="group_id" class="tag small bg-primary text-white rounded px-5 py-3 mb-3 mr-3">
                  {{ getGroupById(group_id) }}
                </span>
              </span>

              <span v-else-if="slotProps.column.field === 'user'" class="d-flex flex-row align-contents-center">
                <el-avatar class="my-auto" :user="slotProps.row"></el-avatar>
                <p class="my-auto ml-8">{{ slotProps.row.first_name }} {{ slotProps.row.last_name }}</p>
              </span>
              <span v-else>
                {{ slotProps.formattedRow[slotProps.column.field] }}
              </span>
            </template>
          </vue-good-table>
        </div>
      </template>
    </workspace-layout>
  </div>
  <use-desktop-version v-else>
    {{ $t('mobile.no_invite') }}
  </use-desktop-version>
</template>

<script>
import WorkspaceLayout from '@/layouts/workspace';

import { ElBreadcrumbs, ElAvatar } from '@/components/elements/_index';

import InvitePopup from '@/popups/general/invite';
import UserRemovePopup from '@/popups/user/remove';
import UpdateContainerDistributionGroupsPopup from '@/popups/container/update-distribution-groups';

import HasForm from '@/components/forms/has-form';

import 'vue-good-table/dist/vue-good-table.css';
import { VueGoodTable } from 'vue-good-table';

export default {
  name: 'views.container.users',

  components: { WorkspaceLayout, ElBreadcrumbs, ElAvatar, VueGoodTable },

  mixins: [HasForm],

  data() {
    return {
      selectedUsers: [],
      columns: [
        { width: '250px', label: this.$t('forms.labels.user'), field: 'user', sortable: true, sortFn: this.userSort },
        { width: '300px', label: this.$t('forms.labels.email'), field: 'email' },
        { width: '200px', label: this.$t('forms.labels.last_activity'), field: 'last_activity', type: 'date', dateInputFormat: 'yyyy-MM-dd HH:mm:ss', dateOutputFormat: 'PP', thClass: 'text-left', tdClass: 'text-left' },
        { width: '200px', label: this.$t('forms.labels.admin_permissions'), field: 'admin', sortable: false },
        { width: '200px', label: 'Group', field: 'group', sortable: false },
        { field: this.$t('forms.labels.lastname'), hidden: true },
        { field: this.$t('forms.labels.firstname'), hidden: true },
      ],
    };
  },

  mounted() {
    this.$store.dispatch('container/readUsers', { container_id: this.$route.params.containerId, force: true });

    this.readDistributionGroups();
  },

  methods: {
    selectionChanged(changes) {
      this.selectedUsers = changes.selectedRows.map(u => u.id);
    },

    inviteUser() {
      this.$modal.show(InvitePopup, { type: 'container', modelId: this.container.id }, { adaptive: true, height: 'auto', width: '600', scrollable: true });
    },

    addDistributionGroup() {
      this.$modal.show(UpdateContainerDistributionGroupsPopup, { containerId: this.container.id }, { adaptive: true, height: 'auto', width: '600', scrollable: true });
    },

    removeUser() {
      let users = this.users
        .filter(u => !this.selectedUsers.includes(u.id))
        .map(u => {
          return { id: u.id, is_container_admin: u.is_container_admin };
        });
      this.$modal.show(UserRemovePopup, { type: 'container', users: users, modelCount: this.selectedUsers.length, modelTitle: this.container.title, modelId: this.container.id }, { adaptive: true, height: 'auto', width: '400' });
    },

    handleUserRoleUpdated(user) {
      this.loading = true;
      this.$store
        .dispatch('container/updateUser', { container_id: this.container.id, user })
        .then(res => {
          this.users = res.reverse();
          this.handleSuccess();
        })
        .catch(e => this.handleError(e));
    },

    readDistributionGroups() {
      if (!this.workspace) {
        return;
      }

      this.$store.dispatch('distributionGroup/readAll', { workspace_id: this.workspace.id });
    },

    getGroupById(groupId) {
      let group = this.distributionGroups.find(group => group.id === groupId);

      if (!group) {
        return;
      }

      return group.title;
    },
  },
  computed: {
    workspace() {
      return this.$store.getters['workspace/current'];
    },
    user() {
      return this.$store.getters['auth/user'];
    },
    users() {
      return this.$store.getters['container/users'];
    },
    container() {
      return this.$store.getters['container/viewing'];
    },
    distributionGroups() {
      return this.$store.getters['distributionGroup/all'];
    },
  },
};
</script>
